/* eslint-disable @typescript-eslint/no-unused-vars */

import { useLazyQuery, useQuery } from '@apollo/client'
import {
  Button,
  CircularProgress,
  Collapse,
  Fade,
  useTheme
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import FilterListIcon from '@material-ui/icons/FilterList'
import React, { useEffect, useState } from 'react'
import InfiniteScroll from 'react-infinite-scroller'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import AdotelSkeleton from '../../components/AdotelSkeleton'
import PageHeader from '../../components/PageHeader'
import SectionTitle from '../../components/SectionTitle'
import { CHANNEL_PUSH_QUERY } from '../../graphql/channelPush'
import { getTranslate } from '../../localization'
import { WebHook, ChannelPushFiltersInput } from '../../types/channelPush'
import { PushDetails } from '../../types/channelPush'
import AccsTabelHead from './AccsTabelHead'
import { AccsTableRow } from './AccsTableRow'
import AdaptorsTableHead from './AdaptorsTableHead'
import { AdaptorsTableRow } from './AdaptorsTableRow'
import AddNewWebHookModal from './AddNewWebHokkModal'
import ChannelPushFilters from './ChannelPushFilters'
import useStyles from './styles/index.styles'

const ChannelPush = () => {
  const classes = useStyles()
  const theme = useTheme()
  const { channelCode } = useParams<{ channelCode: string }>()
  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false)
  const [isEditMode, setisEditMode] = useState<boolean>(false)
  const [filterButtonSelected, setFilterButtonSelected] =
    useState<boolean>(false)
  const [filters, setfilters] = useState<ChannelPushFiltersInput>({
    channelCode: channelCode
  })
  const deafaultWebHookInput: WebHook = {
    adaptor: 'Standard',
    interval: 300,
    state: 'Connected',
    endPoint: 'https://www',
    failureCount: 0,
    lastAttempt: '',
    subscribed: true
  }

  const webHookMenuItems: { label: string; value: string }[] = [
    { label: 'Adotel Connect v.1', value: 'Standard' },
    { label: 'RezGain Bridge v.2', value: 'RateGain' },
    { label: 'Acomotel v.1', value: 'Wozozo' },
    { label: 'SnappTrip v.1', value: 'Snapp' }
  ]
  const valueToLabelMap = new Map(
    webHookMenuItems.map((item) => [item.value, item.label])
  )
  const getWebHookLabelByValue = (value: string): string | undefined => {
    return valueToLabelMap.get(value)
  }

  const [webHookInput, setWebHookInput] =
    useState<WebHook>(deafaultWebHookInput)

  const PAGE_SIZE = 5
  const [numberOfVisibleAccommodations, setNumberOfVisibleAccommodations] =
    useState(PAGE_SIZE)
  const [getChannelPush, { loading, data, refetch }] = useLazyQuery<{
    channelPush: PushDetails
  }>(CHANNEL_PUSH_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    onError: (err) => {
      toast.error(err.message)
    }
  })

  useEffect(() => {
    getChannelPush({
      variables: {
        filters: filters
      }
    })
  }, [])

  let i = 0

  return (
    <div>
      <PageHeader title={getTranslate('ARI Push')} />
      <div className={classes.adaptorsSectionHeader}>
        <SectionTitle title={getTranslate('Web Hooks')} marginBottom />
        <Button
          size={'medium'}
          startIcon={<AddIcon />}
          variant="contained"
          color="primary"
          onClick={() => {
            setModalIsOpen(true)
            setisEditMode(false)
          }}>
          {getTranslate('Add New Web Hook')}
        </Button>
        <AddNewWebHookModal
          isOpen={modalIsOpen}
          onClose={() => {
            setModalIsOpen(false)
          }}
          isEditMode={isEditMode}
          webHookInput={webHookInput}
          setWebHookInput={setWebHookInput}
          deafaultWebHookInput={deafaultWebHookInput}
          refetch={refetch}
          webHookMenuItems={webHookMenuItems}
        />
      </div>
      <div className={classes.adaptorsContainer}>
        <AdaptorsTableHead refetch={refetch} />
        {data?.channelPush.webHooks.map((item, index) => (
          <Fade in timeout={600} key={index}>
            <AdaptorsTableRow
              onClick={() => {
                setModalIsOpen(true)
                setWebHookInput(item)
                setisEditMode(true)
              }}
              getWebHookLabelByValue={getWebHookLabelByValue}
              refetch={refetch}
              data={item}
            />
          </Fade>
        ))}
      </div>
      <div className={classes.accommodationSectionHeader}>
        <SectionTitle title="Accommodations Table" marginBottom />
        <div className={classes.buttons}>
          <Button
            size={'medium'}
            startIcon={<FilterListIcon />}
            variant="contained"
            color="primary"
            onClick={() => {
              setFilterButtonSelected(!filterButtonSelected)
            }}>
            {getTranslate('Filter')}
          </Button>
        </div>
      </div>
      <Collapse in={filterButtonSelected}>
        <ChannelPushFilters
          filters={filters}
          setfilters={setfilters}
          getChannelPush={getChannelPush}
        />
      </Collapse>
      <div className={classes.accommodationsContainer}>
        <AccsTabelHead refetch={refetch} />
        <InfiniteScroll
          pageStart={0}
          loadMore={() => {
            setTimeout(() => {
              setNumberOfVisibleAccommodations(
                numberOfVisibleAccommodations + PAGE_SIZE
              )
            }, 500)
          }}
          hasMore={
            data &&
            numberOfVisibleAccommodations <=
              data.channelPush.accommodations.length - 1
          }
          loader={
            <div key={0} className={classes.loadingContainer}>
              <CircularProgress size={32} />
            </div>
          }
          useWindow={true}>
          {data?.channelPush.accommodations
            .slice(0, numberOfVisibleAccommodations)
            .map((acc, index) =>
              acc.pushs.map((push) => {
                i++
                return (
                  <AccsTableRow
                    key={index}
                    accommodationName={acc.accommodationName}
                    push={push}
                    getWebHookLabelByValue={getWebHookLabelByValue}
                    isOdd={i % 2 !== 0}
                  />
                )
              })
            )}
        </InfiniteScroll>
      </div>
      <AdotelSkeleton
        open={loading}
        circlesColor={theme.palette.primary.main}
        numberOfCircles={4}
      />
    </div>
  )
}

export default ChannelPush
